import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import {
  LogOut,
  MapPin,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  ChevronRight,
  Home,
  User,
  Phone,
  Mail,
  Building,
  Hammer,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_jax-craft-remodel/artifacts/6r3h6sd1_Transparent%20Logo.png";

const statusColors = {
  completed: 'bg-green-100 text-green-700 border-green-200',
  active: 'bg-blue-100 text-blue-700 border-blue-200',
  pursuing: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  pending: 'bg-gray-100 text-gray-700 border-gray-200',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-200'
};

export const CustomerProjectView = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePhaseIndex, setActivePhaseIndex] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    const storedCustomer = localStorage.getItem('customer_data');
    
    if (!token) {
      navigate('/customer/login');
      return;
    }

    if (storedCustomer) {
      setCustomer(JSON.parse(storedCustomer));
    }

    fetchProjectData(token);
  }, [navigate]);

  const fetchProjectData = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/api/customer/project`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectData(response.data);
    } catch (err) {
      console.error('Error fetching project:', err);
      if (err.response?.status === 401) {
        handleLogout();
        toast.error('Session expired. Please log in again.');
      } else {
        setError('Failed to load project data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_data');
    navigate('/customer/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading your project...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="font-heading text-xl mb-2">Error Loading Project</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-[#C6A87C] text-white font-semibold"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { project, phases, documents, company } = projectData || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-project-page">
      {/* Header */}
      <header className="bg-[#0A0A0A] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <img src={LOGO_URL} alt="Jax Remodeling Solutions" className="h-10 w-auto" />
            <div className="flex items-center gap-4">
              <div className="hidden md:block text-right">
                <p className="text-sm font-medium">{customer?.name}</p>
                <p className="text-xs text-gray-400">{customer?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 border border-white/20 text-sm hover:bg-white/10 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden md:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-[#0A0A0A] text-white pb-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="py-8">
            <p className="text-[#C6A87C] text-sm font-semibold uppercase tracking-wider mb-2">
              Your Project
            </p>
            <h1 className="font-heading text-3xl md:text-4xl mb-4">
              {project?.project_type}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-gray-400">
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {project?.address}, {project?.city}
              </span>
              <span className={`px-3 py-1 text-sm font-medium border ${statusColors[project?.status] || statusColors.pending}`}>
                {project?.status?.replace('_', ' ').toUpperCase()}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="bg-white/5 backdrop-blur border border-white/10 p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-400">Overall Progress</span>
              <span className="text-2xl font-heading text-[#C6A87C]">
                {project?.progress_percentage?.toFixed(0) || 0}%
              </span>
            </div>
            <div className="h-3 bg-white/10 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${project?.progress_percentage || 0}%` }}
                transition={{ duration: 1, delay: 0.3 }}
                className="h-full bg-gradient-to-r from-[#C6A87C] to-[#D4B88C]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Project Details & Phases */}
          <div className="lg:col-span-2 space-y-8">
            {/* Project Info */}
            <div className="bg-white border border-gray-100 p-6">
              <h2 className="font-heading text-lg mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-[#C6A87C]" />
                Project Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50">
                  <p className="text-xs uppercase text-gray-400 mb-1">Client</p>
                  <p className="font-medium">{project?.client_name}</p>
                </div>
                <div className="p-4 bg-gray-50">
                  <p className="text-xs uppercase text-gray-400 mb-1">Project Type</p>
                  <p className="font-medium">{project?.project_type}</p>
                </div>
                {project?.start_date && (
                  <div className="p-4 bg-gray-50">
                    <p className="text-xs uppercase text-gray-400 mb-1">Start Date</p>
                    <p className="font-medium">{new Date(project.start_date).toLocaleDateString()}</p>
                  </div>
                )}
                {project?.estimated_completion && (
                  <div className="p-4 bg-gray-50">
                    <p className="text-xs uppercase text-gray-400 mb-1">Est. Completion</p>
                    <p className="font-medium">{new Date(project.estimated_completion).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
              {project?.description && (
                <div className="mt-4 p-4 bg-gray-50">
                  <p className="text-xs uppercase text-gray-400 mb-1">Description</p>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              )}
            </div>

            {/* Phases */}
            {phases && phases.length > 0 && (
              <div className="bg-white border border-gray-100 p-6">
                <h2 className="font-heading text-lg mb-4 flex items-center gap-2">
                  <Hammer className="w-5 h-5 text-[#C6A87C]" />
                  Project Phases
                </h2>

                {/* Phase Tabs */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                  {phases.map((phase, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActivePhaseIndex(idx)}
                      className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                        activePhaseIndex === idx
                          ? 'bg-[#C6A87C] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {phase.name}
                    </button>
                  ))}
                </div>

                {/* Active Phase Content */}
                <div className="space-y-3">
                  {phases[activePhaseIndex]?.tasks?.map((task, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className={`p-4 border ${
                        task.status === 'completed'
                          ? 'border-green-200 bg-green-50'
                          : task.status === 'in_progress'
                          ? 'border-blue-200 bg-blue-50'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full ${
                          task.status === 'completed'
                            ? 'bg-green-500 text-white'
                            : task.status === 'in_progress'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 text-gray-400'
                        }`}>
                          {task.status === 'completed' ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : task.status === 'in_progress' ? (
                            <Clock className="w-4 h-4" />
                          ) : (
                            <span className="text-xs">{idx + 1}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className={`font-medium ${task.status === 'completed' ? 'text-green-700' : ''}`}>
                            {task.name}
                          </p>
                          <p className="text-sm text-gray-500 capitalize">
                            {task.status.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Documents */}
            {documents && documents.length > 0 && (
              <div className="bg-white border border-gray-100 p-6">
                <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#C6A87C]" />
                  Shared Documents
                </h3>
                <div className="space-y-2">
                  {documents.map((doc, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-sm">{doc.name}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(doc.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Info */}
            <div className="bg-white border border-gray-100 p-6">
              <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#C6A87C]" />
                Contact Us
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="font-medium text-lg">{company?.name}</p>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Phone className="w-4 h-4 text-[#C6A87C]" />
                  <span>{company?.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Mail className="w-4 h-4 text-[#C6A87C]" />
                  <span className="text-sm">{company?.email}</span>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100">
                <a
                  href={`mailto:${company?.email}`}
                  className="block w-full py-3 bg-[#C6A87C] text-white text-center text-sm font-semibold uppercase tracking-wider hover:bg-[#B09265] transition-colors"
                >
                  Send Message
                </a>
              </div>
            </div>

            {/* Help Box */}
            <div className="bg-[#0A0A0A] text-white p-6">
              <h3 className="font-heading text-lg mb-2">Need Help?</h3>
              <p className="text-gray-400 text-sm mb-4">
                Our team is here to answer any questions about your project.
              </p>
              <a
                href={`tel:${company?.phone}`}
                className="inline-flex items-center gap-2 text-[#C6A87C] hover:underline text-sm font-medium"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <img src={LOGO_URL} alt="Jax Remodeling Solutions" className="h-8 w-auto mx-auto mb-4" />
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Jax Remodeling Solutions LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
